// ==============================================
// Plan checks: settings that are wrong, or will surprise a customer or a
// boat, shown beside the editor and as a dot on the section in the menu.
// ==============================================

import type { BasemapSource } from '../../../types/subscription';
import { BASEMAP_OPTIONS, type PackageFormData } from './packageForm';

export interface PlanCheck {
  /** A problem is wrong and should be fixed; a warning may be meant. */
  level: 'problem' | 'warning';
  /** The plan section (planSections.ts id) the check belongs to. */
  section: string;
  message: string;
}

/**
 * URL keys the code looks for by name, and what changing each one breaks.
 * Their odd spelling is deliberate, so the tidy-key check leaves them alone.
 */
export const SPECIAL_SLUGS: Record<string, string> = {
  casual: 'Website orders on this plan are priced as a flat fee plus a price per tile, by its URL key "casual".',
  _standard_features: 'This hidden plan holds the "All plans include" list on the pricing page, found by its URL key.',
  Tester: 'Customers on this plan are in the testers group for the new token system, found by its URL key.',
  'Tester - Internal': 'Customers on this plan are in the testers group for the new token system, found by its URL key.',
};

export const isTidySlug = (slug: string) => /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug);
export const suggestSlug = (name: string) => name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Sources whose terms forbid storing their maps on a device. */
const NO_CACHING: BasemapSource[] = ['google', 'bing', 'mapbox', 'here'];

/** Words in a feature bullet, and the setting that must be on for the bullet to be true. */
const BULLET_PROMISES: [RegExp, keyof PackageFormData][] = [
  [/offline/i, 'offline_packs'],
  [/\bais\b/i, 'ais_integration'],
  [/anchor watch|vortex anchor/i, 'vortex_anchor'],
  [/remote/i, 'anchor_remote_view'],
  [/tracking|polaris track/i, 'public_tracking'],
  [/\benc\b/i, 'siitech_marine_enc'],
  [/bathymetry|depth shading/i, 'bathymetry'],
  [/instrument/i, 'instrument_panel'],
  [/truehelm/i, 'truehelm'],
  [/screenshot/i, 'screenshot_library'],
  [/ship'?s papers/i, 'document_library'],
  [/navigation library/i, 'nav_library'],
  [/sd card/i, 'sd_card_charts'],
  [/google drive/i, 'google_drive_charts'],
  [/colour|color/i, 'weather_colour'],
];

export const promisesSwitchedOff = (bullet: string, form: PackageFormData) =>
  BULLET_PROMISES.some(([words, key]) => words.test(bullet) && !form[key]);

export function planChecks(form: PackageFormData): PlanCheck[] {
  const checks: PlanCheck[] = [];
  const add = (level: PlanCheck['level'], section: string, message: string) => checks.push({ level, section, message });

  if (form.slug && !isTidySlug(form.slug) && !SPECIAL_SLUGS[form.slug]) {
    add('problem', 'store', `The URL key "${form.slug}" has spaces or capitals. Suggested: "${suggestSlug(form.name)}".`);
  }
  for (const bullet of form.features) {
    if (promisesSwitchedOff(bullet, form)) add('warning', 'store', `The bullet "${bullet}" promises something this plan has switched off.`);
  }
  if (form.visibility === 'active' && !form.description.trim()) {
    add('warning', 'store', 'The pricing page shows this plan with no description.');
  }

  if (form.cost_per_1000_tokens_cents === 0) {
    add('warning', 'tokens', 'At NZ$0 per 1,000 extra tokens, website chart orders past the allowance are free.');
  }
  if (form.out_of_tokens_policy === 'stop' && !form.token_packs.some((p) => p.on_sale)) {
    add('problem', 'tokens', 'Boats are stopped when they run out, but no top-up pack is on sale.');
  }

  if (form.offline_packs && form.allowed_basemaps_offline.length === 0) {
    add('problem', 'maps', 'Offline downloads are on, but no map source can be downloaded.');
  }
  for (const source of form.offline_packs ? form.allowed_basemaps_offline : []) {
    if (NO_CACHING.includes(source)) {
      const name = BASEMAP_OPTIONS.find((b) => b.value === source)?.label ?? source;
      add('warning', 'maps', `${name} forbids storing its maps on a device. Offline use is for testing only.`);
    }
  }

  if (form.google_webview) {
    add('warning', 'fleet', 'Google web view switches every Scout on this plan at its next restart.');
  }
  return checks;
}
